import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useLanguage } from '../../../contexts/LanguageContext';
import { eventNameFor } from '../../../utils/eventName';
import { getCountryOptions } from '../../../utils/countries';
import { MIN_INTERESTS } from '../../../constants/doctorProfile';
import ProfileFormFields from '../profile/ProfileFormFields';
import * as SelfApi from '../selfApi';
import failureText from '../profile/failureText';

const BLUE = '#47ABDB';

// Same Hero/Grey layout as AttendeeProfile.js / SpeakerDashboard.js, so this
// reads as part of the same product rather than a bolted-on extra step.
const Page = styled.div`margin:1.5rem 1rem 3rem;position:relative;@media(max-width:640px){margin:0 0 2rem;}`;
const Hero = styled.div`position:relative;background:#fff;box-shadow:0 25px 50px -12px rgba(0,0,0,.18);`;
const HeroInner = styled.div`max-width:78rem;margin:0 auto;padding:3.75rem 2.5rem 3.25rem;@media(max-width:600px){padding:2.5rem 1.5rem;}`;
const BigHeadline = styled.h1`margin:0;color:${BLUE};font-weight:800;text-transform:uppercase;letter-spacing:.02em;line-height:1.04;font-size:clamp(2.2rem,5.2vw,3.8rem);`;
const SubHeadline = styled.p`margin:.85rem 0 0;color:#5B5B5B;font-weight:700;font-size:clamp(1rem,1.9vw,1.35rem);`;
const TagLine = styled.p`margin:.5rem 0 0;color:#5B5B5B;font-weight:400;font-style:italic;font-size:clamp(.85rem,1.3vw,1rem);`;
const Grey = styled.div`background:#d0e6ef;`;
const GreyInner = styled.div`max-width:52rem;margin:0 auto;padding:2rem 2.5rem;@media(max-width:640px){padding:1.5rem;}`;
const SuccessBanner = styled.div`background-color:#d1fae5;color:#065f46;padding:1rem;margin-bottom:1.5rem;text-align:center;`;
const Card = styled.div`background:#fff;padding:1.75rem;box-shadow:0 8px 24px rgba(15,23,42,.08);`;
const CardTitle = styled.h2`margin:0 0 .5rem;font-size:1.2rem;color:#111D4E;`;
const Intro = styled.p`color:#334155;margin:0 0 1.5rem;font-size:.95rem;line-height:1.5;`;
const Empty = styled.p`color:#5B6B78;`;

// Landed on right after accepting a speaker invitation (see
// SpeakerInvitationLanding.js) - for a new account the first time they see
// the profile we already hold for them, so they can check it over and fill
// in what is missing before it is shown publicly; for a returning speaker
// it is usually just a receipt. What an invitation proposes is no longer a
// staged draft this page resolves on load: the new model carries it as a
// change set (D9), decided per field on the person's own profile
// (/profile). The one form is shared with that profile's edit popup
// (ProfileFormFields), so the two cannot drift apart.
const SpeakerInviteConfirmation = () => {
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const eventName = eventNameFor(language, location.state?.eventName, location.state?.eventNameEN);
  const countryOptions = useMemo(() => getCountryOptions(language), [language]);
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  // Proposed profile changes are change sets on the new model (D9) and are decided on the profile
  // (/profile), so this page only loads the person's own profile to review and complete it.
  const [fieldDiffs] = useState([]);

  useEffect(() => {
    SelfApi.fetchMe()
      .then((me) => { const loaded = { ...(me.profile || {}), email: me.account?.email }; setProfile(loaded); setDraft(loaded); })
      .catch(() => setProfile({ error: true }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDraft({ ...draft, [name]: value });
  };
  const toggleDraftList = (key, value) => {
    setDraft((current) => ({
      ...current,
      [key]: (current[key] || []).includes(value)
        ? current[key].filter((v) => v !== value)
        : [...(current[key] || []), value],
    }));
  };
  const roleLabel = (status) => t(`speaker.role.${status}`, status.replaceAll('_', ' '));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    if ((draft.interests || []).length < MIN_INTERESTS) {
      setMessage({ text: t('speaker.onboarding.err.interests', 'Please select at least three cardiac interests.'), error: true });
      return;
    }
    setSaving(true);
    try {
      // Only the keys the profile API knows: the loaded profile carries the account's email next to
      // it (for the form's read-only field), which PUT /api/me/profile does not take.
      const profileBody = { ...draft };
      delete profileBody.email;
      await SelfApi.updateProfile(profileBody);
      navigate('/profile');
    } catch (err) {
      console.error('Profile save failed:', err);
      setMessage({ text: failureText(err, t('profile.saveError', 'Could not save your profile. Please try again.')), error: true });
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return <Page><Hero><HeroInner><Empty>{t('profile.loading', 'Loading your profile…')}</Empty></HeroInner></Hero></Page>;
  }
  if (profile.error) {
    return <Page><Hero><HeroInner><Empty>{t('profile.loadError', 'Your profile could not be loaded.')}</Empty></HeroInner></Hero></Page>;
  }

  return (
    <Page>
      <Hero>
        <HeroInner>
          <BigHeadline>YOUR MEDICAL CARE PILOT</BigHeadline>
          <SubHeadline>Build your Best Care with Heartport</SubHeadline>
          <TagLine>Here to support your medical superpowers with research insights and AI-enhanced clinical care tools.</TagLine>
        </HeroInner>
      </Hero>

      <Grey>
        <GreyInner>
          {eventName && (
            <SuccessBanner>
              {t('profile.invitationAccepted', "You've successfully accepted your invitation to speak at {eventName}.", { eventName })}
            </SuccessBanner>
          )}
          <Card>
            <CardTitle>{t('speaker.onboarding.title', 'Review your profile')}</CardTitle>
            <Intro>{t('speaker.onboarding.intro', "Some of this was found through AI research and reviewed by the organizers - please check it over and complete anything that's missing before it's shown publicly.")}</Intro>
            {fieldDiffs.length > 0 && (
              <Intro>{t('speaker.onboarding.diffIntro', 'Fields marked below have a newer value available - check each one and pick whichever you\'d like to keep.')}</Intro>
            )}
            <ProfileFormFields
              draft={draft} countryOptions={countryOptions} message={message} saving={saving}
              t={t} roleLabel={roleLabel}
              onChange={handleChange} onToggleList={toggleDraftList}
              onInterestsChange={(v) => setDraft((current) => ({ ...current, interests: v }))}
              onSubmit={handleSubmit}
              onCancel={() => navigate('/profile')}
              cancelLabel={t('speaker.onboarding.skip', 'Continue without changes')}
              submitLabel={t('speaker.onboarding.continue', 'Save & continue')}
              fieldDiffs={fieldDiffs}
            />
          </Card>
        </GreyInner>
      </Grey>
    </Page>
  );
};

export default SpeakerInviteConfirmation;
